<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3";
import axios from "axios";
import { ref } from "vue";

import Btn from "@/components/ui/atoms/Btn.vue";
import Input from "@/components/ui/atoms/Input.vue";
import MasterBlock from "@/components/ui/atoms/MasterBlock.vue";
import PillContainer from "@/components/ui/atoms/PillContainer.vue";
import { resolveUrl } from "@/utils/urls";

const form = useForm({
  email: "",
});

const openChat = () => {
  HelpCrunch("openChat");
};

const showSuccessMessage = ref(false);
const submitNewsletter = () => {
  axios.post(resolveUrl("newsletter"), form.data()).then((response) => {
    if (response.status === 201) {
      console.log("Subscribed to newsletter");
      showSuccessMessage.value = true;
    }
    form.reset();
  });
};
</script>

<template>
  <footer id="footer">
    <MasterBlock theme="accent">
      <div class="container">
        <div class="footer-content">
          <div class="footer-content__links">
            <div>
              <h3>Contenido</h3>
              <ul>
                <li>
                  <Link :href="resolveUrl('tree_show', 'informatica')">Informática</Link>
                </li>
                <li>
                  <Link :href="resolveUrl('tree_show', 'desarrollo')">Desarrollo</Link>
                </li>
                <li>
                  <Link :href="resolveUrl('tree_show', 'ciberseguridad')">
                    Ciberseguridad
                  </Link>
                </li>
                <li>
                  <Link :href="resolveUrl('tree_show', 'multi-skill')">MultiSkill</Link>
                </li>
              </ul>
            </div>

            <div>
              <h3>Mastermind</h3>
              <ul>
                <li>
                  <Link :href="resolveUrl('course_list')">Cursos</Link>
                </li>
                <li>
                  <a
                    href="mailto:trabajo@mastermind.ac?Subject=Quiero%20trabajar%20en%20Mastermind"
                  >
                    Trabaja con nosotros
                  </a>
                </li>
                <li>
                  <Link :href="resolveUrl('subscription_show')">Suscripción</Link>
                </li>
                <li>
                  <Link href="/#preguntas">Preguntas frecuentes</Link>
                </li>
              </ul>
            </div>

            <div>
              <h3>Soporte</h3>
              <ul>
                <li>
                  <button @click="openChat">Chatea con nosotros</button>
                </li>
                <li>
                  <a href="mailto:soporte@mastermind.ac">Envía un email</a>
                </li>
              </ul>
            </div>

            <div>
              <h3>Legal</h3>
              <ul>
                <li>
                  <Link :href="resolveUrl('term_show', 'privacidad')">
                    Política de privacidad
                  </Link>
                </li>
                <li>
                  <Link :href="resolveUrl('term_show', 'condiciones')">
                    Condiciones de contratación
                  </Link>
                </li>
                <li>
                  <Link :href="resolveUrl('term_show', 'cookies')">
                    Política de cookies
                  </Link>
                </li>
                <li>
                  <Link :href="resolveUrl('term_show', 'legal')">Aviso legal</Link>
                </li>
              </ul>
            </div>
          </div>

          <div class="newsletter">
            <h2>No te pierdas lo último de Mastermind con nuestra newsletter.</h2>
            <PillContainer
              full-width
              :class="['subscribe-to-newsletter-form']"
              tag="form"
              no-padding
              @submit.prevent="submitNewsletter"
            >
              <Input
                v-model="form.email"
                required
                type="email"
                placeholder="Email"
                size="medium"
                theme="dark-wireframe"
                shape="square"
                :high="true"
                :class="['sizeless']"
                full-width
              />
              <Btn
                tag="button"
                type="submit"
                theme="dark-bold"
                shape="round"
                :class="['sizeless']"
                :disabled="form.processing"
              >
                Regístrate
              </Btn>
            </PillContainer>
            <p v-if="showSuccessMessage" class="news-message">
              ¡Muchas gracias por unirte a nuestra newsletter!
            </p>
          </div>
        </div>
        <div class="footer-brand">
          <img src="@img/footer_mm.svg" alt="The challenging tech school" />
        </div>
      </div>
    </MasterBlock>
  </footer>
</template>

<style scoped lang="scss">
@use "@/styles/settings/vars";

#footer {
  margin-top: -35px;
  position: relative;
}

.subscribe-to-newsletter-form {
  position: relative;

  input.pill {
    border: 2px solid vars.$off-black;
  }

  input::placeholder {
    color: vars.$off-black;
  }

  button {
    position: absolute;
    right: 0.5em;
    top: 3px;
    margin: 5px;
    color: vars.$poly-fuchsia;
  }
}

#footer .master-block {
  padding: 0;
}

.footer-content {
  display: flex;
  flex-wrap: wrap;

  &__links {
    width: 60%;
    display: flex;
    gap: 2em;
    flex-wrap: wrap;
    box-sizing: border-box;
    padding-right: 4em;
    margin-bottom: 2em;

    div li {
      margin-bottom: 0.8em;
    }

    div li a,
    h3 {
      text-transform: uppercase;
      font-size: 0.9em;
    }

    button {
      background-color: transparent;
      padding: 0;
      text-transform: uppercase;
      font-size: 0.9em;
      cursor: pointer;
      color: vars.$off-black;
    }

    div {
      ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }
    }
  }
}

.footer-brand {
  overflow: hidden;

  img {
    max-width: 90%;
    float: right;
  }
}

.newsletter {
  width: 40%;
  margin-bottom: 2em;

  h2 {
    text-align: right;
  }

  .news-message {
    font-weight: 500;
    text-align: right;
  }
}

.footer-content__form {
  float: right;
  top: 0;
  right: 0;
  width: 41%;

  h2 {
    font-family: vars.$ff_apparat_medium;
  }
}

@media (max-width: 1024px) {
  .newsletter {
    width: 100%;

    h2 {
      text-align: center;
      margin-bottom: 0;
      font-size: 0.9em;
    }
  }

  .footer-content__links {
    width: 100%;
  }

  .footer-brand {
    img {
      max-width: 70%;
    }
  }

  #footer .container {
    padding-left: 2em;
    padding-right: 2em;
  }
}
</style>
